<template>
  <div class="create-customer-account d-flex flex-column">
    <div class="create-customer-account__scroll">
      <v-form ref="form" v-model="valid" @submit.prevent>
        <v-stepper-vertical hide-actions flat :model-value="currentStep">
          <template #default>
            <!-- Step 1: General details -->
            <v-stepper-vertical-item
              :value="1"
              :elevation="0"
              :complete="currentStep > 1"
              :class="{ 'step-completed': currentStep > 1 }"
            >
              <template #title>
                <div class="subtitle1 step-title">
                  {{ $t("modals.createBackupCustomerAccount.generalDetails.title") }}
                  <template v-if="currentStep > 1">
                    <span
                      class="body2 text-indigo-medium ml-2"
                      data-testid="create-customer-account-step-1-summary"
                    >
                      {{ generalDetailsSummary }}
                    </span>
                  </template>
                </div>
              </template>
              <template #icon>
                <v-icon v-if="currentStep > 1" icon="$check" />
                <span v-else>1</span>
              </template>

              <div class="body2 mb-4">
                {{ $t("modals.createBackupCustomerAccount.generalDetails.subtitle") }}
              </div>

              <v-autocomplete
                v-model="localValue.workspaceId"
                data-testid="create-customer-account-workspace"
                variant="outlined"
                :items="descendants"
                item-title="name"
                :rules="[required()]"
                item-value="workspaceId"
                :loading="descendantsLoading"
                :no-data-text="$t('general.noData')"
                :label="$t('modals.createBackupCustomerAccount.generalDetails.workspace.label')"
                :placeholder="
                  $t('modals.createBackupCustomerAccount.generalDetails.workspace.placeholder')
                "
              />
              <v-text-field
                v-model.number="localValue.limit"
                data-testid="create-customer-account-limit"
                type="number"
                class="mt-2"
                variant="outlined"
                :rules="seatLimitRules"
                persistent-hint
                :hint="$t('modals.createBackupCustomerAccount.generalDetails.limit.hint')"
                :label="$t('modals.createBackupCustomerAccount.generalDetails.limit.label')"
                :placeholder="
                  $t('modals.createBackupCustomerAccount.generalDetails.limit.placeholder')
                "
              >
                <template #message="{ message }">
                  <span class="d-flex align-center">
                    <v-icon size="16" class="mr-1" icon="$infoXs" />
                    <span v-html="message" />
                  </span>
                </template>
              </v-text-field>

              <div class="subtitle1 mt-4 mb-2">
                {{ $t("modals.createBackupCustomerAccount.generalDetails.retention.title") }}
              </div>
              <v-radio-group v-model="localValue.retentionType" hide-details="auto">
                <v-radio
                  v-for="option in retentionOptions"
                  :key="option"
                  :value="option"
                  class="mb-2 align-start"
                  :data-testid="`create-customer-account-retention-${option}`"
                >
                  <template #label>
                    <div class="d-flex flex-column">
                      <span class="body1">
                        {{
                          $t(
                            `modals.createBackupCustomerAccount.generalDetails.retention.options.${option}.title`
                          )
                        }}
                      </span>
                      <span class="body2 text-indigo-medium">
                        {{
                          $t(
                            `modals.createBackupCustomerAccount.generalDetails.retention.options.${option}.description`
                          )
                        }}
                      </span>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-stepper-vertical-item>

            <!-- Step 2: Select cloud service to backup -->
            <v-stepper-vertical-item :value="2" :elevation="0">
              <template #title>
                <div class="subtitle1 step-title">
                  {{ $t("modals.createBackupCustomerAccount.selectWorkloads.title") }}
                </div>
              </template>
              <template #icon>
                <span>2</span>
              </template>

              <div
                class="body2 mb-4"
                v-html="$t('modals.createBackupCustomerAccount.selectWorkloads.info')"
              />

              <div class="workloads-list">
                <div class="workloads-list-item" v-for="workload in workloads" :key="workload">
                  <v-checkbox
                    v-model="localValue.workloadIds"
                    :value="workload"
                    :data-testid="`create-customer-account-workload-${workload}`"
                    class="px-4"
                    hide-details
                    color="primary"
                  >
                    <template #label>
                      <span class="d-flex align-center">
                        <img
                          v-if="workloadIcons[workload]"
                          :src="`/images/app-service-logos/${workloadIcons[workload]}`"
                          :alt="$t(`general.keepitWorkloads.${workload}`)"
                          class="workload-icon mr-4 ml-3"
                        />
                        <span class="body2">
                          {{ $t(`general.keepitWorkloads.${workload}`) }}
                        </span>
                      </span>
                    </template>
                  </v-checkbox>
                </div>
              </div>

              <div
                v-if="showWorkloadsError"
                class="body2 text-error mt-2 d-flex align-center"
                data-testid="create-customer-account-workloads-error"
              >
                <v-icon size="16" class="mr-1" icon="$warning" />
                {{ $t("modals.createBackupCustomerAccount.selectWorkloads.error") }}
              </div>
            </v-stepper-vertical-item>
          </template>
        </v-stepper-vertical>
      </v-form>
    </div>

    <!-- Pinned action bar: buttons stay visible while the fields scroll above. -->
    <div class="create-customer-account__actions">
      <template v-if="currentStep === 1">
        <v-btn
          rounded
          color="primary"
          size="large"
          data-testid="create-customer-account-next-btn"
          @click="onNextStepClick()"
        >
          {{ $t("general.next") }}
        </v-btn>
        <v-btn
          variant="text"
          color="primary"
          size="large"
          data-testid="create-customer-account-cancel-btn"
          @click="$emit('dismissed')"
        >
          {{ $t("general.cancel") }}
        </v-btn>
      </template>
      <template v-else>
        <v-btn
          variant="text"
          color="primary"
          size="large"
          data-testid="create-customer-account-back-btn"
          @click="currentStep = 1"
        >
          {{ $t("general.back") }}
        </v-btn>
        <v-btn
          rounded
          color="primary"
          size="large"
          data-testid="create-customer-account-submit-btn"
          @click="createChild()"
        >
          {{ $t(`modals.${config.action}.actionBtn`) }}
        </v-btn>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { required } from "@/_helpers/validators";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import {
  type BackupDescendant,
  type CreateBackupChildPayload,
  useMspCloudBackupStore,
} from "@/_store/msp-cloud-backup.module.ts";
import { RetentionType, KEEPIT_WORKLOAD_ICONS, KeepitWorkload } from "@/constants/cloud-backup.ts";

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<Partial<CreateBackupChildPayload>>>,
      required: true,
    },
  },
  emits: ["dismissed", "update:localValue"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const backupAndRestoreStore = useMspCloudBackupStore();
    const form = ref();
    const valid = ref(true);
    const showWorkloadsError = ref(false);
    const descendants = ref<BackupDescendant[]>([]);
    const descendantsLoading = ref(false);
    const currentStep = ref(1);

    const localValue = ref<CreateBackupChildPayload>({
      workspaceId: null,
      limit: null,
      retentionType: RetentionType.YEARLY,
      workloadIds: [],
    });

    const seatLimitRules = [
      (value: number | string | null | undefined) => {
        if (value === undefined || value === null || `${value}`.trim() === "") {
          return true;
        }
        const numeric = Number(value);
        if (!Number.isInteger(numeric)) {
          return i18n.t("validations.onlyInteger");
        }
        if (numeric < 1) {
          return i18n.t("validations.minNumberAllowed", { n: 1 });
        }
        return true;
      },
    ];

    const generalDetailsSummary = computed(() => {
      const limit = localValue.value.limit;
      const seatLimit = !limit
        ? i18n.t("modals.createBackupCustomerAccount.generalDetails.limit.noLimit")
        : limit;
      return `${localValue.value.workspaceId}, ${i18n.t(
        "modals.createBackupCustomerAccount.generalDetails.limit.label"
      )}: ${seatLimit}`;
    });

    const onNextStepClick = async () => {
      const validationResult = await form.value?.validate();
      if (validationResult?.valid) {
        currentStep.value = 2;
      }
    };

    const createChild = async () => {
      showWorkloadsError.value = !localValue.value.workloadIds?.length;
      if (showWorkloadsError.value) {
        return;
      }
      if (props.config.callback) {
        props.config.callback(localValue.value);
        emit("dismissed");
      }
    };

    watch(
      () => localValue.value.workloadIds,
      (workloadIds) => {
        if (workloadIds?.length) {
          showWorkloadsError.value = false;
        }
      }
    );

    watch(
      localValue,
      (value) => {
        emit("update:localValue", value);
      },
      { deep: true }
    );

    onMounted(async () => {
      descendantsLoading.value = true;
      descendants.value = await backupAndRestoreStore.getDescendants();
      descendantsLoading.value = false;
    });

    return {
      form,
      valid,
      showWorkloadsError,
      descendants,
      descendantsLoading,
      currentStep,
      localValue,
      seatLimitRules,
      generalDetailsSummary,
      onNextStepClick,
      createChild,
      retentionOptions: Object.values(RetentionType),
      workloads: Object.values(KeepitWorkload),
      workloadIcons: KEEPIT_WORKLOAD_ICONS,
      required,
    };
  },
});
</script>

<style lang="scss" scoped>
.create-customer-account {
  max-height: 72vh;
}

.create-customer-account__scroll {
  flex: 1 1 auto;
  // min-height:0 lets this flex child shrink below its content size so
  // overflow-y can actually engage instead of stretching the modal.
  min-height: 0;
  overflow-y: auto;
  padding-right: 8px;
}

.create-customer-account__actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  margin-top: 8px;
  border-top: 1px solid rgb(var(--v-theme-indigo-faint));
}

.workloads-list {
  border-radius: 8px !important;
  border: 1px solid rgb(var(--v-theme-indigo-pale));
  max-height: 240px;
  overflow: auto;

  &-item {
    height: 40px;
    margin-top: 8px;
  }
}

.workload-icon {
  width: 35px;
  height: 35px;
  flex: 0 0 auto;
}

:deep(.v-stepper-vertical) {
  box-shadow: unset !important;
  background-color: transparent;
}

:deep(*) {
  .icon-check-xs:before {
    color: rgba(var(--v-theme-white));
  }

  .v-stepper-vertical-item__avatar.v-avatar {
    background: rgb(var(--v-theme-indigo-pale));
    color: rgb(var(--v-theme-primary));
  }

  .v-expansion-panel--active .v-stepper-vertical-item__avatar.v-avatar {
    background: rgb(var(--v-theme-indigo-medium));
    color: rgb(var(--v-theme-white));
  }

  .step-completed {
    .v-avatar {
      background: rgb(var(--v-theme-success)) !important;
    }
  }
}
</style>
